import React from 'react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiFileText, FiBriefcase, FiMapPin, FiUploadCloud, FiArrowRight, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { API_BASE_URL } from '../utils/constant';

// Framer Motion Variants for step transitions
const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
};

const JobApplicationForm = () => {
    const location = useLocation();
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        job: '',
        name: '',
        email: '',
        phone: '',
        resume: null,
        coverLetter: '',
        dob: '',
        gender: '',
        address: '',
        country: 'India', // Default to India
        state: '',
        city: '',
        zipCode: '',
        // These fields might come from your API later
        // skill: [], 
        // customQuestion: [],
    });

    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Pre-fill Job ID from navigation state
    useEffect(() => {
        if (location.state?.job) {
            setFormData(prev => ({ ...prev, job: location.state.job.id }));
        }
    }, [location.state]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            setFormData({ ...formData, resume: files[0] });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (step < 3) {
            nextStep();
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccessMessage('');

        const formDataToSend = new FormData();
        for (const key in formData) {
            if (formData[key]) {
                formDataToSend.append(key, formData[key]);
            }
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-apply`, {
                method: 'POST',
                body: formDataToSend,
            });
            const data = await response.json();
            if (response.ok) {
                setSuccessMessage(data.message || 'Application submitted successfully!');
                // Reset form completely after success
                setFormData({ job: '', name: '', email: '', phone: '', resume: null, coverLetter: '', dob: '', gender: '', address: '', country: 'India', state: '', city: '', zipCode: '' });
                setStep(1);
            } else {
                setError(data.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const nextStep = () => setStep(prev => Math.min(prev + 1, 3));
    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
    
    const steps = ["Personal Details", "Professional Information", "Address"];

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl w-full space-y-8">
                <div className="text-center">
                    <h2 className="text-3xl font-extrabold text-slate-900">Apply for this Position</h2>
                    <p className="mt-2 text-md text-slate-600">Please complete the form below to apply.</p>
                </div>
                
                {/* Progress Bar */}
                <div className="px-4">
                    <div className="flex items-center">
                        {steps.map((s, index) => (
                            <React.Fragment key={index}>
                                <div className="flex flex-col items-center relative">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${step > index + 1 ? 'bg-blue-600 text-white' : step === index + 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                        {step > index + 1 ? <FiCheckCircle size={20} /> : index + 1}
                                    </div>
                                    <p className={`mt-2 text-xs font-semibold absolute -bottom-6 transition-colors duration-300 ${step >= index + 1 ? 'text-blue-600' : 'text-slate-500'}`}>{s}</p>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`flex-auto border-t-2 transition-colors duration-300 ${step > index + 1 ? 'border-blue-600' : 'border-slate-200'}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="mt-8 bg-white shadow-xl rounded-2xl p-8 border border-slate-200/80">
                    {successMessage ? (
                        <div className="text-center py-8">
                             <FiCheckCircle className="mx-auto text-green-500 text-5xl mb-4"/>
                             <h3 className="text-2xl font-bold text-slate-800">Application Submitted!</h3>
                             <p className="text-slate-600 mt-2">{successMessage}</p>
                        </div>
                    ) : (
                    <form onSubmit={handleSubmit}>
                        <AnimatePresence mode="wait">
                            <motion.div key={step} variants={stepVariants} initial="hidden" animate="visible" exit="exit" transition={{ duration: 0.3 }}>
                                {step === 1 && <Step1 formData={formData} handleChange={handleChange} />}
                                {step === 2 && <Step2 formData={formData} handleChange={handleChange} handleFileDrop={handleFileDrop} />}
                                {step === 3 && <Step3 formData={formData} handleChange={handleChange} />}
                            </motion.div>
                        </AnimatePresence>

                        {error && <div className="text-red-600 text-center mt-4 text-sm font-medium">{error}</div>}

                        {/* Navigation Buttons */}
                        <div className="mt-8 flex justify-between items-center">
                            <button type="button" onClick={prevStep} disabled={step === 1} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                <FiArrowLeft/> Previous
                            </button>
                            <button type="submit" disabled={isSubmitting} className="inline-flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-slate-400 transition-colors shadow-sm">
                                {isSubmitting ? 'Submitting...' : step === 3 ? 'Submit Application' : 'Next'}
                                {!isSubmitting && step < 3 && <FiArrowRight/>}
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            </div>
        </div>
    );
};


// --- Sub-components for each step ---

const Step1 = ({ formData, handleChange }) => (
    <div className="space-y-6">
        <InputField label="Job ID" name="job" value={formData.job} Icon={FiBriefcase} onChange={handleChange} type="number" required readOnly={!!formData.job} />
        <InputField label="Full Name" name="name" value={formData.name} Icon={FiUser} onChange={handleChange} required />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Email Address" name="email" value={formData.email} Icon={FiMail} onChange={handleChange} type="email" required />
            <InputField label="Phone Number" name="phone" value={formData.phone} Icon={FiPhone} onChange={handleChange} required />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Date of Birth" name="dob" value={formData.dob} onChange={handleChange} type="date" />
             <SelectField label="Gender" name="gender" value={formData.gender} onChange={handleChange} options={["Male", "Female", "Other", "Prefer not to say"]} />
        </div>
    </div>
);

const Step2 = ({ formData, handleChange, handleFileDrop }) => (
     <div className="space-y-6">
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Resume / CV</label>
            <div 
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 border-dashed rounded-md"
            >
                <div className="space-y-1 text-center">
                    <FiUploadCloud className="mx-auto h-12 w-12 text-slate-400"/>
                    <div className="flex text-sm text-slate-600">
                        <label htmlFor="resume" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                            <span>Upload a file</span>
                            <input id="resume" name="resume" type="file" className="sr-only" onChange={handleChange} accept=".pdf,.doc,.docx" />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                    </div>
                    {formData.resume?.name ? (
                        <p className="text-xs text-green-600 font-semibold">{formData.resume.name}</p>
                    ) : (
                        <p className="text-xs text-slate-500">PDF, DOC, DOCX up to 10MB</p>
                    )}
                </div>
            </div>
        </div>
         <div>
            <label htmlFor="coverLetter" className="block text-sm font-medium text-slate-700">Cover Letter (Optional)</label>
            <textarea id="coverLetter" name="coverLetter" value={formData.coverLetter} onChange={handleChange} rows="4" className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"></textarea>
        </div>
     </div>
);

const Step3 = ({ formData, handleChange }) => (
    <div className="space-y-6">
        <InputField label="Address" name="address" value={formData.address} Icon={FiMapPin} onChange={handleChange} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Country" name="country" value={formData.country} onChange={handleChange} />
            <InputField label="State" name="state" value={formData.state} onChange={handleChange} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="City" name="city" value={formData.city} onChange={handleChange} />
            <InputField label="Zip / Postal Code" name="zipCode" value={formData.zipCode} onChange={handleChange} />
        </div>
    </div>
);

// --- Reusable Input Field Component ---

const InputField = ({ label, name, value, Icon, onChange, type = 'text', required = false, readOnly = false }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500">*</span>}</label>
        <div className="mt-1 relative rounded-md shadow-sm">
            {Icon && <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center"><Icon className="text-slate-400" size={18}/></div>}
            <input type={type} id={name} name={name} value={value} onChange={onChange} required={required} readOnly={readOnly}
                   className={`block w-full h-12 ${Icon ? 'pl-10' : 'px-3'} py-2 bg-white border ${readOnly ? 'bg-slate-100' : ''} border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} />
        </div>
    </div>
);

const SelectField = ({ label, name, value, onChange, options = [] }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-700">{label}</label>
        <select id={name} name={name} value={value} onChange={onChange} className="mt-1 block w-full h-12 pl-3 pr-10 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
            <option value="">Select...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);


export default JobApplicationForm;